import { useMutation, useSuspenseQuery } from '@tanstack/react-query';

import type { Estate } from '@/features/estates-property/api/estate';
import type { MutationConfig } from '@/lib/react-query';
import { api, extractData } from '@/lib/treaty';

import type { Project } from './projects';

type LinkProjectEstateOptions = {
  mutationConfig?: MutationConfig<typeof linkProjectEstate>;
};

const linkProjectEstate = ({
  projectId: id,
  estateId,
}: {
  projectId: string;
  estateId: string;
}) => {
  return extractData(api.projects({ id }).estates.put({ estateId }));
};

export const useLinkProjectEstate = ({ mutationConfig }: LinkProjectEstateOptions) => {
  return useMutation({ ...mutationConfig, mutationFn: linkProjectEstate });
};

// --------- unlink estate from project ----------
type UnlinkEstateFromProjectOptions = {
  mutationConfig?: MutationConfig<typeof unlinkEstateFromProject>;
};

const unlinkEstateFromProject = ({
  projectId: id,
  estateId,
}: {
  projectId: Project['id'];
  estateId: Estate['id'];
}) => {
  return extractData(api.projects({ id }).estates({ estateId }).delete());
};

export const useUnlinkEstateFromProject = ({ mutationConfig }: UnlinkEstateFromProjectOptions) => {
  return useMutation({ ...mutationConfig, mutationFn: unlinkEstateFromProject });
};

// --------- get project estates ----------
export type ProjectEstatesQueryFilters = Parameters<
  ReturnType<typeof api.projects>['estates']['get']
>[number];

export type ProjectEstates = Awaited<ReturnType<typeof getProjectEstates>>['estates'];

type ProjectEstatesQueryOptions = ProjectEstatesQueryFilters & {
  projectId: Project['id'];
};

export const getProjectEstates = ({ projectId, ...args }: ProjectEstatesQueryOptions) => {
  return extractData(api.projects({ id: projectId }).estates.get(args));
};

export const getProjectEstatesOptions = ({ projectId, ...args }: ProjectEstatesQueryOptions) => ({
  queryKey: ['projects', 'estates', projectId, args.query],
  queryFn: () => {
    if (projectId) {
      return getProjectEstates({ projectId, ...args });
    } else {
      return {
        estates: [],
        count: 0,
      };
    }
  },
});

export const useProjectEstates = (args: ProjectEstatesQueryOptions) => {
  return useSuspenseQuery({ ...getProjectEstatesOptions(args) });
};
